import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { SettingsProvider } from "@/contexts/SettingsContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { Toaster } from "sonner";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import FleetPage from "@/pages/FleetPage";
import ClientsPage from "@/pages/ClientsPage";
import RentalsPage from "@/pages/RentalsPage";
import CalendarPage from "@/pages/CalendarPage";
import MaintenancePage from "@/pages/MaintenancePage";
import SettingsPage from "@/pages/SettingsPage";
import ContractsArchivePage from "@/pages/ContractsArchivePage";
import ReportsPage from "@/pages/ReportsPage";
import ChatPage from "@/pages/ChatPage";
import FirmaKioskPage from "@/pages/FirmaKioskPage";
import KioskClientiPage from "@/pages/KioskClientiPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AppLayout from "@/components/AppLayout";
import "@/App.css";
import { useState, useEffect } from "react";
import axios from "axios";

const SiteHome = lazy(() => import("@/site/HomePage"));
const SiteFleet = lazy(() => import("@/site/FleetPublicPage"));
const SiteVehicle = lazy(() => import("@/site/VehiclePublicPage"));
const WebsiteAdminPage = lazy(() => import("@/pages/WebsiteAdminPage"));

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-slate-500">Caricamento...</p>
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <AppLayout>{children}</AppLayout>;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
};

const suspense = (el) => <Suspense fallback={<div className="min-h-screen bg-[#0b0b0c]" />}>{el}</Suspense>;

let siteEnabledCache = null;
const useSiteEnabled = () => {
  const [enabled, setEnabled] = useState(siteEnabledCache);
  useEffect(() => {
    if (siteEnabledCache !== null) return;
    axios.get("/api/public/site")
      .then((r) => { siteEnabledCache = r.data?.content?.site_enabled !== false; setEnabled(siteEnabledCache); })
      .catch(() => { siteEnabledCache = true; setEnabled(true); });
  }, []);
  return enabled;
};

const SiteGate = ({ page }) => {
  const enabled = useSiteEnabled();
  if (enabled === null) return <div className="min-h-screen bg-[#0b0b0c]" />;
  if (!enabled) return page === "home" ? <PublicRoute><LoginPage /></PublicRoute> : <Navigate to="/" replace />;
  if (page === "fleet") return suspense(<SiteFleet />);
  if (page === "vehicle") return suspense(<SiteVehicle />);
  return suspense(<SiteHome />);
};

function AppRoutes() {
  return (
    <Routes>
      {/* Sito vetrina pubblico (disattivabile da Sito Web -> Impostazioni) */}
      <Route path="/" element={<SiteGate page="home" />} />
      <Route path="/flotta" element={<SiteGate page="fleet" />} />
      <Route path="/flotta/:slug" element={<SiteGate page="vehicle" />} />
      {/* Gestionale */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/veicoli" element={<ProtectedRoute><FleetPage /></ProtectedRoute>} />
      <Route path="/clienti" element={<ProtectedRoute><ClientsPage /></ProtectedRoute>} />
      <Route path="/noleggi" element={<ProtectedRoute><RentalsPage /></ProtectedRoute>} />
      <Route path="/manutenzione" element={<ProtectedRoute><MaintenancePage /></ProtectedRoute>} />
      <Route path="/impostazioni" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/calendario" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
      <Route path="/archivio" element={<ProtectedRoute><ContractsArchivePage /></ProtectedRoute>} />
      <Route path="/report" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
      <Route path="/chat" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
      <Route path="/sito-web" element={<ProtectedRoute>{suspense(<WebsiteAdminPage />)}</ProtectedRoute>} />
      <Route path="/firma-kiosk" element={<FirmaKioskPage />} />
      <Route path="/kiosk-clienti" element={<KioskClientiPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <SettingsProvider>
          <AuthProvider>
            <AppRoutes />
            <Toaster position="top-right" richColors closeButton />
          </AuthProvider>
        </SettingsProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
